import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronLeft, WifiOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { AudioButton } from '../../components/AudioButton.tsx'
import { EmptyState } from '../../components/EmptyState.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { ApiError } from '../../lib/api.ts'
import { clearAudioError, stopAudio, useAudio } from '../../lib/audio.ts'
import { TODAY_KEY } from '../home/today.ts'
import { Amalkan } from './Amalkan.tsx'
import { STEPS, ayatKey, isStep, useAyat, useLocalSet, type AyatDetail, type CompleteResponse, type StepId } from './ayat.ts'
import { Celebration } from './Celebration.tsx'
import { Fahami } from './Fahami.tsx'
import { Iqra } from './Iqra.tsx'
import { Renungi } from './Renungi.tsx'

function Stepper({ day, step, done }: { day: number; step: StepId; done: (s: StepId) => boolean }) {
  const idx = STEPS.findIndex((s) => s.id === step)
  const all = STEPS.every((s) => done(s.id))
  return (
    <nav className="stepper" aria-label="Tahap ayat">
      <ol>
        {STEPS.map((s, i) => {
          const on = s.id === step
          const ok = done(s.id) && !on
          return (
            <li key={s.id} className={on ? 'on' : ok ? 'done' : undefined}>
              <Link to={`/ayat/${day}/${s.id}`} aria-current={on ? 'step' : undefined}>
                <span className="dot" aria-hidden="true">{ok ? <Check size={14} strokeWidth={3} /> : i + 1}</span>
                <span>{s.label}{ok && <span className="sr">, sudah</span>}</span>
              </Link>
            </li>
          )
        })}
      </ol>
      <div className="track" aria-hidden="true"><i style={{ width: `${all ? 100 : idx * 25}%` }} /></div>
    </nav>
  )
}

function Flow({ d, day, step }: { d: AyatDetail; day: number; step: StepId }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const audio = useAudio()
  const [visited, visit] = useLocalSet(`iqp_steps_${day}`)
  const [celebrate, setCelebrate] = useState<CompleteResponse | null>(null)
  const a = d.ayat
  const completed = Boolean(d.journal?.completedAt)
  const srcs = (a.verses ?? []).flatMap((v) => (v.audio ? [v.audio] : []))

  useEffect(() => { visit(step) }, [step, visit])
  useEffect(() => { document.documentElement.scrollTop = 0 }, [step])
  useEffect(() => {
    if (!audio.failed) return
    const t = setTimeout(clearAudioError, 2600)
    return () => clearTimeout(t)
  }, [audio.failed])

  const onDone = (r: CompleteResponse) => {
    setCelebrate(r)
    void qc.invalidateQueries({ queryKey: TODAY_KEY })
    void qc.invalidateQueries({ queryKey: ayatKey(day) })
  }

  return (
    <div className="ayat">
      <header className="ayat-head">
        <div className="ayat-bar">
          <Link className="icon-btn" to="/" aria-label="Kembali ke Beranda"><ChevronLeft size={22} /></Link>
          <div className="ayat-ttl">
            <small>Ayat ke-{day}{a.tema ? ` · ${a.tema}` : ''}</small>
            <h1>{a.ref}</h1>
          </div>
          {srcs.length > 0 && <AudioButton id={`ayat-${day}`} srcs={srcs} what={srcs.length > 1 ? 'semua ayat' : 'ayat'} className="on-dark" compact />}
        </div>
        <Stepper day={day} step={step} done={(s) => completed || visited.has(s)} />
      </header>

      <div className="ayat-body">
        {step === 'iqra' && <Iqra d={d} day={day} />}
        {step === 'fahami' && <Fahami d={d} day={day} />}
        {step === 'renungi' && <Renungi d={d} day={day} />}
        {step === 'amalkan' && <Amalkan d={d} day={day} onDone={onDone} />}
      </div>

      <div className="toast-slot" role="status">
        {audio.failed && <p className="toast">Suara belum bisa diputar. Cek koneksi, lalu coba lagi.</p>}
      </div>

      {celebrate && <Celebration result={celebrate} onClose={() => navigate('/')} />}
    </div>
  )
}

function Problem({ e, retry }: { e: unknown; retry: () => void }) {
  const back = <Link className="btn btn-ghost" to="/">Kembali ke Beranda</Link>
  if (e instanceof ApiError && e.status === 403) {
    return <EmptyState obj="jam-pasir" title="Belum terbuka" action={back}>{e.message}</EmptyState>
  }
  if (e instanceof ApiError && e.status === 404) {
    return <EmptyState obj="buku" title="Ayat belum tersedia" action={back}>Ayat untuk hari ini belum disiapkan. Coba lagi nanti, ya.</EmptyState>
  }
  return (
    <EmptyState Icon={WifiOff} title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={retry}>Coba lagi</button>}>
      {e instanceof ApiError ? e.message : 'Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.'}
    </EmptyState>
  )
}

function AyatDay({ day, step }: { day: number; step: StepId }) {
  const q = useAyat(day)
  useEffect(() => stopAudio, []) // leaving the ayat stops its audio

  if (q.isPending) {
    return (
      <div className="ayat" aria-busy="true" aria-label="Memuat ayat">
        <header className="ayat-head"><div className="ayat-bar" style={{ minHeight: 56 }} /></header>
        <div className="ayat-body"><SkCard ar lines={3} h={260} /></div>
      </div>
    )
  }
  if (q.isError) return <div className="page"><Problem e={q.error} retry={() => q.refetch()} /></div>
  return <Flow d={q.data} day={day} step={step} />
}

/** /ayat/:day/:tahap — the 4 steps of a day (docs/PRD.md §6.4). `/ayat/:day` opens Iqra'. */
export function AyatPage() {
  const { day: raw, tahap } = useParams()
  const day = Number(raw)
  if (!Number.isInteger(day) || day < 1) return <Navigate to="/" replace />
  if (!isStep(tahap)) return <Navigate to={`/ayat/${day}/iqra`} replace />
  return <AyatDay key={day} day={day} step={tahap} />
}
